import React, { PropsWithChildren, ErrorInfo } from 'react';

import { addReactError } from '@datadog/browser-rum-react';
import { MtxButton, MtxIcon, MtxTypography } from '@meilleurtaux/mtp-uikit-react';
import { t } from 'i18next';

import BrandLogo from '@/assets/icons/BrandLogo.svg?react';
import BrandLogoMobile from '@/assets/icons/brandLogoMobile.svg?react';
import { URLS } from '@/constants/urls';

import styles from './styles.module.css';

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends React.Component<PropsWithChildren, ErrorBoundaryState> {
  constructor(props: PropsWithChildren) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    addReactError(error, errorInfo);
  }

  private readonly handleRetry = () => {
    globalThis.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className={styles.container}>
          <div className={styles.brandContainer}>
            <a href={URLS.MTP_URL}>
              <BrandLogo
                className={styles.brandWrapper}
                width={300}
                height={44}
                aria-label="Logo Meilleurtaux Placement"
              />
              <BrandLogoMobile
                className={styles.brandMobileWrapper}
                width={44}
                height={42}
                aria-label="Logo Meilleurtaux Placement"
              />
            </a>
          </div>
          <div className={styles.errorContainer}>
            <div className={styles.errorWrapper}>
              <MtxIcon className={styles.errorIcon} name="warning-solide" size={96} />
              <MtxTypography variant="title-26" weight="bold">
                <span>{t('errorBoundary.errorTitle')}</span>
              </MtxTypography>
              <MtxTypography variant="body-16">
                <span>{t('errorBoundary.loadingErrorMessage')}</span>
              </MtxTypography>
              <MtxTypography variant="body-16">
                <span>{t('errorBoundary.retryLaterMessage')}</span>
              </MtxTypography>
            </div>
            <div className={styles.buttonContainer}>
              <MtxButton size="large" fullWidth onClick={this.handleRetry}>
                {t('common.tryAgain')}
              </MtxButton>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
